import { useState } from "react";
import { MessageCircle, Phone, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CLINIC, waLink } from "../lib/clinic";
import api from "../lib/api";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

const Appointment = () => {
  usePageMeta({
    title: "Book Appointment | Dr. Shantanu Kumar Gupta, Varanasi",
    path: "/appointment",
    description: "Book an eye specialist consultation with Dr. Shantanu Kumar Gupta in Varanasi. WhatsApp for fastest confirmation. Same-day appointments often available.",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Appointment", path: "/appointment" }])]));
  const [form, setForm] = useState({ name: "", phone: "", email: "", concern: "", preferred_date: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.concern) {
      toast.error("Please fill name, phone and concern.");
      return;
    }
    setLoading(true);
    try {
      // Strip empty optional fields to satisfy EmailStr validation
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      await api.post("/appointment", payload);
      toast.success("Request received — redirecting you to WhatsApp");
      const msg =
        `Hello Dr. Shantanu,%0A%0A` +
        `I would like to book an appointment.%0A%0A` +
        `Name: ${form.name}%0A` +
        `Phone: ${form.phone}%0A` +
        (form.email ? `Email: ${form.email}%0A` : "") +
        `Concern: ${form.concern}%0A` +
        (form.preferred_date ? `Preferred date: ${form.preferred_date}%0A` : "") +
        (form.message ? `Notes: ${form.message}%0A` : "");
      setTimeout(() => {
        window.open(`https://wa.me/${CLINIC.whatsappRaw}?text=${msg}`, "_blank");
      }, 400);
    } catch (e) {
      toast.error("Could not submit — please try WhatsApp directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="appointment-page">
      <section className="pt-20 pb-10 container-page grid lg:grid-cols-2 gap-16">
        <div>
          <div className="overline mb-4">Book Appointment</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
            Let's take a proper look at your eyes.
          </h1>
          <p className="mt-5 prose-elegant">
            Share a few details and we'll continue on WhatsApp for the fastest confirmation.
            Prefer to call? {CLINIC.phone}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <a href={waLink()} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary" data-testid="appointment-whatsapp-btn">
              <MessageCircle size={16} /> Message on WhatsApp
            </a>
            <a href={`tel:${CLINIC.phoneRaw}`} className="btn-ghost" data-testid="appointment-call-btn">
              <Phone size={14} /> Call the Clinic
            </a>
          </div>

          <div className="mt-12 border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40">
            <div className="overline mb-2">Clinic timings</div>
            {CLINIC.timings.map((t) => (
              <div key={t.day} className="text-sm text-[hsl(var(--foreground))]/80"><span className="font-medium">{t.day}: </span>{t.hours}</div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="border border-border rounded-2xl p-8 bg-card space-y-5" data-testid="appointment-form">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="apt-name" value={form.name} onChange={set("name")} required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" data-testid="apt-phone" value={form.phone} onChange={set("phone")} required />
            </div>
            <div>
              <Label htmlFor="email">Email (optional)</Label>
              <Input id="email" type="email" data-testid="apt-email" value={form.email} onChange={set("email")} />
            </div>
          </div>
          <div>
            <Label htmlFor="concern">Reason for visit</Label>
            <Input id="concern" data-testid="apt-concern" value={form.concern} onChange={set("concern")} placeholder="e.g. Cataract second opinion" required />
          </div>
          <div>
            <Label htmlFor="date">Preferred date (optional)</Label>
            <Input id="date" type="date" data-testid="apt-date" value={form.preferred_date} onChange={set("preferred_date")} />
          </div>
          <div>
            <Label htmlFor="message">Additional notes</Label>
            <Textarea id="message" data-testid="apt-message" rows={4} value={form.message} onChange={set("message")} />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="apt-submit">
            <Send size={16} /> {loading ? "Sending…" : "Send & Continue on WhatsApp"}
          </button>
          <p className="text-xs text-muted-foreground text-center">By submitting you agree to be contacted regarding your appointment.</p>
        </form>
      </section>
    </div>
  );
};

export default Appointment;
